import { useNavigate, useParams } from "react-router-dom";
import { useContext, useEffect, useRef, useState } from 'react';
import { GlobalContext } from "components/GlobalContext";
import { Card, Col, Form, Row } from "react-bootstrap";
import IPost, { IPostCreateOrUpdate, IPostInitResult } from "models/sql/IPost";
import PostService from "services/PostService";
import { generateLog } from "utilities/MiscUtilities";
import MarkdownEditor from "components/MarkdownEditor";
import { useBeforeUnload } from "utilities/UIUtilities";
import DetailFooter from "components/DetailFooter";
import FileUploadEntry from "components/FileUploadEntry";

interface IPostsDetailProps {
    baseData?: IPostInitResult;
}

const PostsDetail = (props: IPostsDetailProps) => {
    const globalContext = useContext(GlobalContext);
    const globalContextRef = useRef(globalContext);
    globalContextRef.current = globalContext;
    const { id }: any = useParams();
    const [post, setPost] = useState<IPost>({
        postId: -1,
        headline: "",
        shortTitle: "",
        shortDescription: "",
        mediaUrl: "",
        bodyContent: "",
        contentLibrary: false,
        productPage: false,
        pageTitle: "",
        metaDescription: "",
        metaImageUrl: "",
        tags: "",
        active: false,
        author: "",
        created: undefined,
        notes: "",
        systemDataJson: ""
    });
    const navigate = useNavigate();
    const [hasFormDataChanged, setHasFormDataChanged] = useState(false);

    useBeforeUnload(
        hasFormDataChanged,
        "You have unsaved changes, please save them before leaving the page."
    );

    useEffect(() => {
        (async () => {
            if (id !== undefined) {
                globalContext.setLoading(true);

                const accessToken = await globalContext.getAccessTokenAsync();
                const result = await PostService.GetAsync(accessToken, id);

                if (result.record) {
                    setPost(result.record);
                } else {
                    globalContext.showErrorMessage("Unable to load Post");
                }

                globalContext.setLoading(false);
            }
        })();
    }, [id]);

    const deleteAsync = async () => {
        if (post && post.postId && window.confirm("Are you SURE you want to delete this?")) {
            globalContext.setLoading(true);
            const accessToken = await globalContext.getAccessTokenAsync();
            await PostService.DeleteAsync(accessToken, post.postId);
            globalContext.setLoading(false);
            navigate("/Posts");
        }
    };

    const submitFormAsync = async () => {
        if (post) {
            globalContext.setLoading(true);
            const accessToken = await globalContext.getAccessTokenAsync();

            const data: IPostCreateOrUpdate = {
                postId: post.postId,
                headline: post.headline,
                shortTitle: post.shortTitle,
                shortDescription: post.shortDescription,
                mediaUrl: post.mediaUrl,
                bodyContent: post.bodyContent,
                contentLibrary: post.contentLibrary,
                productPage: post.productPage,
                pageTitle: post.pageTitle,
                metaDescription: post.metaDescription,
                metaImageUrl: post.metaImageUrl,
                tags: post.tags,
                active: post.active,
                author: post.author,
                created: post.created,
                notes: post.notes,
                systemDataJson: post.systemDataJson
            };

            await PostService.CreateOrUpdateAsync(accessToken, data);
            globalContext.setLoading(false);
            setHasFormDataChanged(false);

            if (data.postId === -1) {
                navigate(-1);
            }
        }
    };

    if (post) {
        return (
            <>
                <Row>
                    <Col>
                        <Card className="mb-2 mt-2">
                            <Card.Body>
                                <Card.Title>
                                    {id !== undefined && <strong>{post.headline}</strong>}
                                    {id === undefined && <strong>New Post</strong>}
                                    {
                                        post.pageTitle &&
                                        <>
                                            <br />
                                            <a href={`/post/${post.pageTitle}`} target="_blank">View Post</a>
                                        </>
                                    }
                                </Card.Title>

                                <Row className="mt-3 mb-3 ms-1 me-1 p-2" style={{ border: "1px solid black", borderRadius: "0.375rem;" }}>
                                    <Row className="align-items-end">
                                        <Col>
                                            <Form.Group className="mb-3" >
                                                <Form.Label>Published</Form.Label>
                                                {
                                                    <Form.Select
                                                        onChange={async (e) => {
                                                            setPost({ ...post, active: e.target.value == "true" });
                                                            setHasFormDataChanged(true);
                                                        }}
                                                        value={post.active ? "true" : "false"}>
                                                        <option key={"empty"} value={""}>-</option>
                                                        {
                                                            ["true", "false"].map((s, index) => <option key={s} value={s}>{s}</option>)
                                                        }
                                                    </Form.Select>
                                                }
                                            </Form.Group>
                                        </Col>
                                        <Col>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Web Address Title</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                        setPost({ ...post, pageTitle: e.target.value });
                                                        setHasFormDataChanged(true);
                                                    }}
                                                    value={post.pageTitle}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Row className="align-items-end">
                                        <Col>
                                            <FileUploadEntry
                                                title="Thumbnail Image"
                                                defaultValue={post.metaImageUrl}
                                                onChange={newVal => {
                                                    setPost({ ...post, metaImageUrl: newVal });
                                                    setHasFormDataChanged(true);
                                                }}
                                            />
                                        </Col>
                                        <Col>
                                            <Form.Group className="mb-3">
                                                <Form.Label>SEO Description</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                        setPost({ ...post, metaDescription: e.target.value });
                                                        setHasFormDataChanged(true);
                                                    }}
                                                    value={post.metaDescription}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Row className="align-items-start">
                                        <Col>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Short Title (under thumbnail)</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    maxLength={28}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                        setPost({ ...post, shortTitle: e.target.value });
                                                        setHasFormDataChanged(true);
                                                    }}
                                                    value={post.shortTitle}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col>
                                            <Form.Group className="mb-3" >
                                                <Form.Label>Short Description (under thumbnail)</Form.Label>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={4}
                                                    maxLength={80}
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            // @ts-ignore
                                                            setPost({ ...post, shortDescription: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={(post?.shortDescription || "")}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Row>


                                <Row className="mt-3 mb-3 ms-1 me-1 p-2" style={{ border: "1px solid black", borderRadius: "0.375rem;" }}>
                                    <Row className="align-items-end">
                                        <Col>
                                            <Form.Group className="mb-3" >
                                                <Form.Label>Article Title</Form.Label>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={4}
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            // @ts-ignore
                                                            setPost({ ...post, headline: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={(post?.headline || "")}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row className="align-items-end">
                                        <Col>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Author</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                        setPost({ ...post, author: e.target.value });
                                                        setHasFormDataChanged(true);
                                                    }}
                                                    value={post.author}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row className="align-items-end">
                                        <Col>
                                            <FileUploadEntry
                                                title={<>Hero media <i>(Comma seperated list of image and youtube video urls)</i></>}
                                                allowMultiple={true}
                                                rows={4}
                                                defaultValue={post.mediaUrl}
                                                onChange={newVal => {
                                                    setPost({ ...post, mediaUrl: newVal });
                                                    setHasFormDataChanged(true);
                                                }}
                                            />
                                        </Col>
                                    </Row>
                                    <Row className="align-items-end">
                                        <Col>
                                            <Form.Group className="mb-3" >
                                                <Form.Label>Body Content</Form.Label>
                                                <MarkdownEditor
                                                    globalContextRef={globalContextRef.current}
                                                    allowUploads={true}
                                                    noun="Content"
                                                    height={600}
                                                    onChange={newVal => {
                                                        setPost({ ...post, bodyContent: newVal });
                                                        setHasFormDataChanged(true);
                                                    }}
                                                    value={post?.bodyContent || ""}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row className="align-items-end">
                                        <Col>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Tags</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                        setPost({ ...post, tags: e.target.value });
                                                        setHasFormDataChanged(true);
                                                    }}
                                                    value={post.tags}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Row>

                                {
                                    globalContext.roles.some(r => r === "Web.Admin") &&
                                    <Row className="align-items-end">
                                        <Col>
                                            <Card className="mb-2">
                                                <Card.Body>
                                                    <Card.Title>
                                                        <strong>Log</strong><br />
                                                    </Card.Title>
                                                    {generateLog<IPost>(post)}
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>
                                }
                                {
                                    globalContext.roles.some(r => r === "Web.Admin") &&
                                    <Row>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Admin Notes</Form.Label>
                                            <MarkdownEditor
                                                globalContextRef={globalContextRef.current}
                                                allowUploads={false}
                                                noun="Note"
                                                onChange={newVal => {
                                                    setPost({ ...post, notes: newVal });
                                                    setHasFormDataChanged(true);
                                                }}
                                                value={post.notes || ""}
                                            />
                                        </Form.Group>
                                    </Row>
                                }
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {
                    globalContext.roles.some(r => r === "Web.Admin") &&
                    <DetailFooter
                        hasFormDataChanged={hasFormDataChanged}
                        deleteAsync={deleteAsync}
                        submitFormAsync={submitFormAsync}
                    />
                }
            </>
        );
    } else {
        return (
            <>
                <h1>There was an issue retrieving the post, please try again.</h1>
            </>
        );
    }
}

export default PostsDetail;
